/**
 * deepl 域 Zod 共享契约 — admin 全局单条 DeepL API Key（集成页配置，纯 DB 加密落库）
 *
 * - 整个 admin 系统共用**一个**全局 DeepL Key（非按 App / 非按账号）；加密存 deepl_credentials
 *   （AES-GCM，master key=env DEEPL_KEY_ENC_KEY），明文永不回显，只回 key_tail 掩码。
 * - 全站翻译（MultiLangFormRows「从英文翻译」/ 书籍文案 / 反馈内容）的 Key 源 = 本凭证（移除 env DEEPL_API_KEY）。
 * admin-own 契约，dev-docs 收录义务 #229（同步提 dev-docs PR）。
 */

import { z } from 'zod';

/** DeepL 凭证连接状态闭集：connected 已验证 / unverified 已存未验 / invalid 验证失败（Key 被拒）*/
export const DEEPL_CREDENTIAL_STATUSES = ['connected', 'unverified', 'invalid'] as const;
export type DeeplCredentialStatus = (typeof DEEPL_CREDENTIAL_STATUSES)[number];

/**
 * PUT /integrations/deepl/credential 入参：录 / 换全局 DeepL API Key。
 * 明文 Key 仅提交、永不回显（响应只回 key_tail 掩码）。DeepL key 无固定前缀（free key 以 `:fx` 结尾、
 * pro key 无），故仅校验非空 + 最小长度，有效性由后端 ping /v2/usage 验证。
 */
export const UpsertDeeplCredentialRequestSchema = z.object({
  api_key: z.string().trim().min(8, 'DeepL API Key 过短'),
});
export type UpsertDeeplCredentialRequest = z.infer<typeof UpsertDeeplCredentialRequestSchema>;

/**
 * 全局 DeepL 凭证视图（GET / PUT / test 返回）。
 * 未配置时 configured=false 且 key_tail/status/时间均为 null；明文 Key 永不出现，只回 key_tail 掩码。
 */
export type DeeplCredentialResponse = {
  configured: boolean;
  /** Key 后 5 位掩码（未配置为 null）*/
  key_tail: string | null;
  status: DeeplCredentialStatus | null;
  last_verified_at: string | null;
  updated_at: string | null;
};

/** 测试连接结果（POST /integrations/deepl/credential/test）*/
export type DeeplCredentialTestResponse = {
  status: DeeplCredentialStatus;
  last_verified_at: string | null;
};
